import { useEffect, useMemo, useRef, useState } from 'react';
import { PlusIcon, SearchIcon, WrenchIcon, PencilIcon, TrashIcon, ChevronDown, Check } from 'lucide-react';
import clsx from 'clsx';
import SourceSelectionModal from '../components/SourceSelectionModal';
import { ServiceUploadModal } from '../components/ServiceUploadModal';
import EditServiceModal from '../components/EditServiceModal';
import { ConfirmationModal, PageLoader } from '../components/common';
import { tenantApi, Service, ServiceRequest } from '../lib/tenant-api';
import { useEventNotifier } from '../contexts/EventNotifierContext';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'inactive', label: 'Inactive' },
];

const SOURCE_OPTIONS = [
  { value: 'upload', label: 'Upload' },
  { value: 'hubspot', label: 'Hubspot' },
  { value: 'google_drive', label: 'Google drive' },
  { value: 'evolved_wp', label: 'Evolved WP URL' },
  { value: 'webflow', label: 'Webflow lawma integration' },
  { value: 'shopify', label: 'Shopify webstore' },
];

const PRICING_OPTIONS = [
  { value: 'fixed', label: 'Fixed price' },
  { value: 'hourly', label: 'Hourly rate' },
  { value: 'monthly', label: 'Monthly subscription' },
  { value: 'yearly', label: 'Yearly subscription' },
  { value: 'custom', label: 'Custom' },
];

const SERVICES_TOOLBAR_HEIGHT = 'h-10';

function serviceHaystack(service: Service): string {
  return `${service.source_file_name || ''} ${service.category || ''}`.toLowerCase();
}

function serviceMatchesSourceFilter(service: Service, filterValue: string): boolean {
  const haystack = serviceHaystack(service);

  switch (filterValue) {
    case 'upload':
      return !haystack.includes('hubspot') &&
        !haystack.includes('google') &&
        !haystack.includes('evolved') &&
        !haystack.includes('wordpress') &&
        !haystack.includes('webflow') &&
        !haystack.includes('shopify');
    case 'hubspot':
      return haystack.includes('hubspot');
    case 'google_drive':
      return haystack.includes('google');
    case 'evolved_wp':
      return haystack.includes('evolved') || haystack.includes('wordpress') || haystack.includes(' wp');
    case 'webflow':
      return haystack.includes('webflow');
    case 'shopify':
      return haystack.includes('shopify');
    default:
      return false;
  }
}

function getServiceSourceLabel(service: Service): string {
  const haystack = serviceHaystack(service);

  if (haystack.includes('hubspot')) return 'Hubspot';
  if (haystack.includes('google')) return 'Google drive';
  if (haystack.includes('evolved') || haystack.includes('wordpress') || haystack.includes(' wp')) {
    return 'Evolved WP URL';
  }
  if (haystack.includes('webflow')) return 'Webflow lawma integration';
  if (haystack.includes('shopify')) return 'Shopify webstore';

  return 'Upload';
}

function formatPricingLabel(pricingModel?: string): string {
  const option = PRICING_OPTIONS.find((item) => item.value === pricingModel);
  if (option) return option.label;
  if (!pricingModel) return 'Custom';
  return pricingModel.charAt(0).toUpperCase() + pricingModel.slice(1);
}

function formatStatusLabel(status: string): string {
  return STATUS_OPTIONS.find((item) => item.value === status)?.label ||
    status.charAt(0).toUpperCase() + status.slice(1);
}

function FilterDropdown({
  buttonLabel,
  panelTitle,
  options,
  selected,
  onApply,
}: {
  buttonLabel: string;
  panelTitle: string;
  options: { value: string; label: string }[];
  selected: string[];
  onApply: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string[]>(selected);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPending(selected);
  }, [selected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setPending(selected);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selected]);

  const toggleValue = (value: string) => {
    setPending((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const triggerLabel =
    selected.length === 0
      ? buttonLabel
      : selected.length === 1
        ? options.find((option) => option.value === selected[0])?.label || buttonLabel
        : `${selected.length} selected`;

  return (
    <div ref={containerRef} className="relative w-full shrink-0 lg:w-36 xl:w-40">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={clsx(
          'inline-flex w-full items-center justify-between rounded-sm bg-gray-100 px-3 text-sm font-medium text-gray-700 hover:bg-gray-200',
          SERVICES_TOOLBAR_HEIGHT
        )}
      >
        <span className="truncate">{triggerLabel}</span>
        <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 text-gray-500" />
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 w-60 rounded-sm border border-gray-200 bg-gray-50 p-4 shadow-md">
          <p className="mb-3 text-sm font-semibold text-gray-900">{panelTitle}</p>
          <div className="max-h-52 space-y-2.5 overflow-y-auto">
            {options.map((option) => {
              const isChecked = pending.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleValue(option.value)}
                  className="flex w-full cursor-pointer items-center gap-3 text-left"
                >
                  <span
                    className={clsx(
                      'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                      isChecked
                        ? 'border-indigo-600 bg-indigo-600 text-white'
                        : 'border-gray-300 bg-white'
                    )}
                  >
                    {isChecked && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <span className="text-sm text-gray-600">{option.label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => {
                onApply(pending);
                setOpen(false);
              }}
              className="rounded-sm bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ServicesManagement() {
  const [services, setServices] = useState<Service[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedPricing, setSelectedPricing] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [serviceToEdit, setServiceToEdit] = useState<Service | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { subscribe } = useEventNotifier();

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribe('refresh', (data) => {
      if (data.message === 'services') {
        loadServices();
      }
    });

    return () => unsubscribe();
  }, [subscribe]);

  const loadServices = async () => {
    try {
      setIsLoading(true);
      const data = await tenantApi.getServices();
      setServices(data);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : err instanceof Error
            ? err.message
            : 'Failed to load services';
      setError(message || 'Failed to load services');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteService = async () => {
    if (!serviceToDelete) return;

    try {
      setIsDeleting(true);
      await tenantApi.deleteService(serviceToDelete.id);

      try {
        await tenantApi.deleteEmbeddings('service', serviceToDelete.id);
      } catch (embeddingError) {
        console.warn(`Failed to delete embeddings for service ${serviceToDelete.id}:`, embeddingError);
      }

      await loadServices();
      setDeleteModalOpen(false);
      setServiceToDelete(null);
      setError(null);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : err instanceof Error
            ? err.message
            : 'Failed to delete service';
      setError(message || 'Failed to delete service');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditService = (service: Service) => {
    setServiceToEdit(service);
    setEditModalOpen(true);
  };

  const handleSaveService = async (serviceData: ServiceRequest) => {
    if (!serviceToEdit) return;

    await tenantApi.updateService(serviceToEdit.id, serviceData);
    await loadServices();
    setEditModalOpen(false);
    setServiceToEdit(null);
    setError(null);
  };

  const handleAddService = () => {
    setIsSourceModalOpen(true);
  };

  const handleManualUpload = () => {
    setIsSourceModalOpen(false);
    setIsUploadModalOpen(true);
  };

  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      const matchesSearch =
        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (service.description && service.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (service.category && service.category.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus =
        selectedStatuses.length === 0 || selectedStatuses.includes(service.status);

      const matchesSource =
        selectedSources.length === 0 ||
        selectedSources.some((filterValue) => serviceMatchesSourceFilter(service, filterValue));

      const pricingValue = service.pricing_model || 'custom';
      const matchesPricing =
        selectedPricing.length === 0 || selectedPricing.includes(pricingValue);

      return matchesSearch && matchesStatus && matchesSource && matchesPricing;
    });
  }, [services, searchTerm, selectedStatuses, selectedSources, selectedPricing]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSourceColor = (service: Service) => {
    const label = getServiceSourceLabel(service);
    switch (label) {
      case 'Upload':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Hubspot':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Google drive':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Evolved WP URL':
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'Webflow lawma integration':
        return 'bg-violet-100 text-violet-800 border-violet-200';
      case 'Shopify webstore':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPricingColor = () => 'bg-pink-100 text-pink-800 border-pink-200';

  const EmptyState = () => (
    <div className="py-12 text-center">
      <WrenchIcon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-4 text-lg font-medium text-gray-900">No services found</h3>
      <p className="mx-auto mt-2 max-w-sm text-sm text-gray-500">
        Start building your service catalog. Add service descriptions, pricing, and details to help
        customers understand what you offer.
      </p>
      <div className="mt-6">
        <button
          type="button"
          onClick={handleAddService}
          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          Add Service
        </button>
      </div>
    </div>
  );

  const SearchEmptyState = () => (
    <div className="py-12 text-center">
      <SearchIcon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-4 text-lg font-medium text-gray-900">No services match your filters</h3>
      <p className="mt-2 text-sm text-gray-500">Try adjusting your search or filter selections.</p>
      <div className="mt-6">
        <button
          type="button"
          onClick={() => {
            setSearchTerm('');
            setSelectedStatuses([]);
            setSelectedSources([]);
            setSelectedPricing([]);
          }}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Clear filters
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Services</h1>
          <p className="mt-2 text-sm text-gray-600">Manage your service offerings and information</p>
        </div>
        <button
          type="button"
          onClick={handleAddService}
          className="inline-flex shrink-0 items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          Add Service
        </button>
      </div>

      <div className="rounded-md border border-gray-200 bg-white p-2 shadow">
        <div className="flex flex-col gap-2 xl:flex-row xl:items-stretch">
          <div className="relative min-w-0 flex-1">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={clsx(
                'w-full rounded-sm border-0 bg-gray-50 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0',
                SERVICES_TOOLBAR_HEIGHT
              )}
              placeholder="Search service"
            />
          </div>
          <FilterDropdown
            buttonLabel="All Status"
            panelTitle="Filter by Status"
            options={STATUS_OPTIONS}
            selected={selectedStatuses}
            onApply={setSelectedStatuses}
          />
          <FilterDropdown
            buttonLabel="All sources"
            panelTitle="Filter by Source"
            options={SOURCE_OPTIONS}
            selected={selectedSources}
            onApply={setSelectedSources}
          />
          <FilterDropdown
            buttonLabel="All prices"
            panelTitle="Filter by Price"
            options={PRICING_OPTIONS}
            selected={selectedPricing}
            onApply={setSelectedPricing}
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex h-[400px] items-center justify-center">
          <PageLoader />
        </div>
      ) : services.length === 0 ? (
        <EmptyState />
      ) : filteredServices.length === 0 ? (
        <SearchEmptyState />
      ) : (
        <div className="space-y-4">
          {filteredServices.map((service) => (
            <div
              key={service.id}
              className="rounded-md border border-gray-200 bg-white p-4 hover:bg-gray-50"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-bold text-gray-900">{service.name}</h3>
                  <p className="mt-2 line-clamp-4 text-sm text-gray-600">{service.description}</p>

                  <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-gray-500">
                    <span className="inline-flex items-center gap-2">
                      <span className="font-medium text-gray-600">Status:</span>
                      <span
                        className={clsx(
                          'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                          getStatusColor(service.status)
                        )}
                      >
                        {formatStatusLabel(service.status)}
                      </span>
                    </span>

                    <span className="inline-flex items-center gap-2">
                      <span className="font-medium text-gray-600">Source:</span>
                      <span
                        className={clsx(
                          'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                          getSourceColor(service)
                        )}
                      >
                        {getServiceSourceLabel(service)}
                      </span>
                    </span>

                    <span className="inline-flex items-center gap-2">
                      <span className="font-medium text-gray-600">Pricing:</span>
                      <span
                        className={clsx(
                          'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                          getPricingColor()
                        )}
                      >
                        {formatPricingLabel(service.pricing_model)}
                      </span>
                    </span>

                    <span className="inline-flex items-center gap-2">
                      <span className="font-medium text-gray-600">Created:</span>
                      <span className="text-gray-700">
                        {new Date(service.created_at).toLocaleDateString()}
                      </span>
                    </span>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleEditService(service)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                    title="Edit service"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setServiceToDelete(service);
                      setDeleteModalOpen(true);
                    }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-red-600"
                    title="Delete service"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <SourceSelectionModal
        isOpen={isSourceModalOpen}
        onClose={() => setIsSourceModalOpen(false)}
        contentType="services"
        onManualUpload={handleManualUpload}
      />

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteService}
        title="Delete Service"
        message={`Are you sure you want to delete the service "${serviceToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {serviceToEdit && (
        <EditServiceModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleSaveService}
          service={serviceToEdit}
        />
      )}

      <ServiceUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadComplete={() => {
          loadServices();
          setIsUploadModalOpen(false);
        }}
      />
    </div>
  );
}
